import config from '../../config';
import { getAuthHeaders, handleResponse, handleNoContentResponse } from './common';

const adminApi = {
    fetchLaborEpisodeReviewQueue: async (params, signal) => {
        const res = await fetch(`${config.api.url}/admin/labor-episode-review?${new URLSearchParams(params)}`, {
            method: 'GET', headers: getAuthHeaders(), signal, cache: 'no-store'
        });
        return handleResponse(res);
    },

    fetchLaborEpisodeReviewDetail: async (pregnancyId, signal) => {
        const res = await fetch(`${config.api.url}/admin/labor-episode-review/${encodeURIComponent(pregnancyId)}`, {
            method: 'GET', headers: getAuthHeaders(), signal, cache: 'no-store'
        });
        return handleResponse(res);
    },

    fetchAuditSummary: async (params, signal) => {
        const res = await fetch(`${config.api.url}/admin/audit-logs/summary?${new URLSearchParams(params)}`, {
            headers: getAuthHeaders(), signal
        });
        return handleResponse(res);
    },

    fetchAuditEvents: async (params, signal) => {
        const res = await fetch(`${config.api.url}/admin/audit-logs?${new URLSearchParams(params)}`, {
            headers: getAuthHeaders(), signal
        });
        return handleResponse(res);
    },
    fetchPregnancyDuplicates: async () => {
        const res = await fetch(`${config.api.url}/admin/pregnancy-duplicates`, { headers: getAuthHeaders() });
        return handleResponse(res);
    },

    mergePregnancyDuplicates: async (request) => {
        const res = await fetch(`${config.api.url}/admin/pregnancy-duplicates/merge`, {
            method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(request)
        });
        return handleResponse(res);
    },

    dismissPregnancyDuplicates: async (request) => {
        const res = await fetch(`${config.api.url}/admin/pregnancy-duplicates/dismiss`, {
            method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(request)
        });
        return handleResponse(res);
    },

    askPracticeAboutPregnancyDuplicates: async (request) => {
        const res = await fetch(`${config.api.url}/admin/pregnancy-duplicates/ask-practice`, {
            method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(request)
        });
        return handleResponse(res);
    },

    // Fetch all users for admin management
    fetchUsers: async () => {
        const res = await fetch(`${config.api.url}/admin/users`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    createGlobalAdmin: async ({ firstName, lastName, email }) => {
        const res = await fetch(`${config.api.url}/admin/users/global-admin`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ firstName, lastName, email })
        });
        return handleResponse(res);
    },

    // Fetch admin statistics
    fetchStats: async ({ excludeSandbox = true, window = '30' } = {}) => {
        const url = new URL(`${config.api.url}/admin/stats`);
        url.searchParams.set('excludeSandbox', String(excludeSandbox));
        if (window) url.searchParams.set('window', window);
        const res = await fetch(url.toString(), {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    // Fetch Stripe subscription billing schedule for admin billing review
    fetchBillingSubscriptionSchedule: async ({ includeCanceled = false } = {}) => {
        const url = new URL(`${config.api.url}/admin/billing/subscription-schedule`);
        if (includeCanceled) {
            url.searchParams.set('includeCanceled', 'true');
        }
        const res = await fetch(url.toString(), {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    // Check whether user organizations can be moved back into sandbox mode
    fetchSandboxConversionCandidates: async (userIds) => {
        const res = await fetch(`${config.api.url}/admin/sandbox-conversion/candidates`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ userIds })
        });
        return handleResponse(res);
    },

    // Convert a user's current organization from live mode back to sandbox mode
    convertOrganizationToSandbox: async (userId, { sandboxDays = 10 } = {}) => {
        const res = await fetch(
            `${config.api.url}/admin/users/${encodeURIComponent(userId)}/convert-organization-to-sandbox`,
            {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ sandboxDays })
            }
        );

        if (res.status === 409) {
            const errorText = await res.text();
            let data = null;
            try {
                data = errorText ? JSON.parse(errorText) : null;
            } catch {
                data = errorText;
            }
            const candidate = data?.candidate || data;
            const blockingReasons = Array.isArray(candidate?.blockingReasons) ? candidate.blockingReasons : [];
            const message = blockingReasons.length
                ? blockingReasons.join(' ')
                : data?.message || data?.detail || errorText || 'Sandbox conversion is blocked.';
            const error = new Error(message);
            error.status = res.status;
            error.statusText = res.statusText;
            error.data = data;
            error.candidate = candidate;
            throw error;
        }

        return handleResponse(res);
    },

    setOrganizationRecordsOnlyAccess: async (organizationReferenceId, { enabled, reason }) => {
        const res = await fetch(
            `${config.api.url}/admin/organizations/${encodeURIComponent(organizationReferenceId)}/records-only-access`,
            {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ enabled, reason })
            }
        );
        return handleResponse(res);
    },

    // Toggle user active/inactive status
    toggleUserStatus: async (userId, isActive) => {
        const res = await fetch(`${config.api.url}/admin/users/${userId}/status`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify({
                userId: parseInt(userId),
                isActive: isActive
            })
        });
        return handleResponse(res);
    },

    // Get user details for admin view
    getUserDetails: async (userId) => {
        const res = await fetch(`${config.api.url}/admin/users/${userId}`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    // Update user role (for future use)
    updateUserRole: async (userId, role) => {
        const res = await fetch(`${config.api.url}/admin/users/${userId}/role`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify({ role })
        });
        return handleResponse(res);
    },

    // Update user email address
    updateUserEmail: async (userId, email) => {
        const res = await fetch(`${config.api.url}/admin/users/${userId}/email`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify({ email })
        });
        return handleResponse(res);
    },

    // Update user organization role
    updateUserOrgRole: async (userId, organizationRole) => {
        const res = await fetch(`${config.api.url}/admin/users/${userId}/organization-role`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify({ organizationRole })
        });
        return handleResponse(res);
    },

    /** System-admin only: preview a global-account consolidation. */
    previewAccountMerge: async ({ sourceUserReferenceId, targetUserReferenceId }) => {
        const res = await fetch(`${config.api.url}/admin/account-merges/preview`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ sourceUserReferenceId, targetUserReferenceId })
        });
        return handleResponse(res);
    },

    /** System-admin only: consolidate the source account into the surviving target account. */
    mergeAccounts: async ({ sourceUserReferenceId, targetUserReferenceId }) => {
        const res = await fetch(`${config.api.url}/admin/account-merges`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ sourceUserReferenceId, targetUserReferenceId })
        });
        return handleResponse(res);
    },

    // System Notification APIs
    getSystemNotification: async () => {
        const res = await fetch(`${config.api.url}/admin/system-notification`, {
            headers: getAuthHeaders()
        });
        if (res.status === 204) return null;
        return handleResponse(res);
    },

    createSystemNotification: async (message) => {
        const res = await fetch(`${config.api.url}/admin/system-notification`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ message })
        });
        return handleResponse(res);
    },

    clearSystemNotification: async () => {
        const res = await fetch(`${config.api.url}/admin/system-notification`, {
            method: 'DELETE',
            headers: getAuthHeaders()
        });
        return handleNoContentResponse(res);
    },

    /** Admin-only: permanently remove a support ticket and its attachments. */
    adminDeleteTicket: async (ticketReferenceId) => {
        const res = await fetch(`${config.api.url}/admin/tickets/${ticketReferenceId}`, {
            method: 'DELETE',
            headers: getAuthHeaders()
        });
        return handleNoContentResponse(res);
    },

    /**
     * Admin-only: list all facilities (including unassigned / legacy global rows).
     * GET /admin/facilities
     */
    fetchAdminFacilities: async () => {
        const res = await fetch(`${config.api.url}/admin/facilities`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    /**
     * Admin-only: list organizations for assignment dropdowns.
     * GET /admin/organizations
     */
    fetchAdminOrganizations: async () => {
        const res = await fetch(`${config.api.url}/admin/organizations`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    /**
     * Admin-only: list each organization's configured fax numbers.
     * GET /admin/organizations/fax-numbers
     */
    fetchOrganizationFaxNumbers: async () => {
        const res = await fetch(`${config.api.url}/admin/organizations/fax-numbers`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    /**
     * Admin-only: query fax transmission outcomes across organizations.
     * GET /admin/fax-results
     */
    fetchAdminFaxResults: async (params = {}) => {
        const query = new URLSearchParams();
        if (params.organizationId != null && params.organizationId !== '') {
            query.set('organizationId', String(params.organizationId));
        }
        if (params.unroutedOnly) query.set('unroutedOnly', 'true');
        if (params.status) query.set('status', params.status);
        if (params.direction) query.set('direction', params.direction);
        if (params.search) query.set('search', params.search);
        if (params.from) query.set('from', params.from);
        if (params.to) query.set('to', params.to);
        if (params.page) query.set('page', String(params.page));
        if (params.pageSize) query.set('pageSize', String(params.pageSize));

        const qs = query.toString();
        const res = await fetch(`${config.api.url}/admin/fax-results${qs ? `?${qs}` : ''}`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    /**
     * Admin-only: set which organization owns a facility (or clear with null).
     * PUT /admin/facilities/{facilityId}/organization
     */
    assignFacilityOrganization: async (facilityId, organizationId) => {
        const res = await fetch(
            `${config.api.url}/admin/facilities/${encodeURIComponent(facilityId)}/organization`,
            {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    organizationId: organizationId === '' || organizationId === undefined ? null : organizationId
                })
            }
        );
        return handleNoContentResponse(res);
    },

    /**
     * Admin-only: assign many facilities to the same organization (or clear with null).
     * POST /admin/facilities/bulk-assign-organization
     * @param {number|null|undefined} organizationId - Target org, or null to unassign
     * @param {Array<number|string>} facilityIds - Facility ids to move
     */
    bulkAssignFacilitiesOrganization: async (organizationId, facilityIds) => {
        const res = await fetch(`${config.api.url}/admin/facilities/bulk-assign-organization`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({
                organizationId: organizationId === '' || organizationId === undefined ? null : organizationId,
                facilityIds: Array.isArray(facilityIds)
                    ? facilityIds
                          .map((id) => (typeof id === 'string' ? parseInt(id, 10) : id))
                          .filter((n) => n != null && !Number.isNaN(n))
                    : []
            })
        });
        return handleNoContentResponse(res);
    },

    /** Search clients by chart or portal login email. GET /admin/clients/portal-access/search */
    searchPortalAccessClients: async (email) => {
        const url = new URL(`${config.api.url}/admin/clients/portal-access/search`);
        url.searchParams.set('email', email);
        const res = await fetch(url.toString(), {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    /** Full portal access diagnostics for a client. GET /admin/clients/{clientReferenceId}/portal-access */
    getClientPortalAccess: async (clientReferenceId) => {
        const res = await fetch(
            `${config.api.url}/admin/clients/${encodeURIComponent(clientReferenceId)}/portal-access`,
            { headers: getAuthHeaders() }
        );
        return handleResponse(res);
    },

    /** Active invite link only. GET /admin/clients/{clientReferenceId}/portal-invite */
    getActivePortalInvite: async (clientReferenceId) => {
        const res = await fetch(
            `${config.api.url}/admin/clients/${encodeURIComponent(clientReferenceId)}/portal-invite`,
            { headers: getAuthHeaders() }
        );
        return handleResponse(res);
    },

    /** First-time invite (fails if active invite exists). POST /admin/clients/{clientReferenceId}/portal-invite */
    createPortalInvite: async (clientReferenceId, options = {}) => {
        const {
            sendEmail = false,
            expirationDays,
            expiresAt
        } = options;
        const body = { sendEmail };
        if (expiresAt) body.expiresAt = expiresAt;
        else if (expirationDays != null) body.expirationDays = expirationDays;

        const res = await fetch(
            `${config.api.url}/admin/clients/${encodeURIComponent(clientReferenceId)}/portal-invite`,
            {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(body)
            }
        );
        return handleResponse(res);
    },

    /** Regenerate invite (expires active, creates new). POST .../portal-invite/regenerate */
    regeneratePortalInvite: async (clientReferenceId, options = {}) => {
        const {
            sendEmail = false,
            expirationDays,
            expiresAt
        } = options;
        const body = { sendEmail };
        if (expiresAt) body.expiresAt = expiresAt;
        else if (expirationDays != null) body.expirationDays = expirationDays;

        const res = await fetch(
            `${config.api.url}/admin/clients/${encodeURIComponent(clientReferenceId)}/portal-invite/regenerate`,
            {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(body)
            }
        );
        return handleResponse(res);
    },

    /** Update portal login email. PUT /admin/clients/{clientReferenceId}/portal-login-email */
    updatePortalLoginEmail: async (clientReferenceId, { email, syncChartEmail = true }) => {
        const res = await fetch(
            `${config.api.url}/admin/clients/${encodeURIComponent(clientReferenceId)}/portal-login-email`,
            {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify({ email, syncChartEmail })
            }
        );
        return handleResponse(res);
    },

    /** Generate password reset link. POST /admin/clients/{clientReferenceId}/password-reset */
    createPasswordReset: async (clientReferenceId, options = {}) => {
        const { sendEmail = false, expirationHours, expiresAt } = options;
        const body = { sendEmail };
        if (expiresAt) body.expiresAt = expiresAt;
        else if (expirationHours != null) body.expirationHours = expirationHours;

        const res = await fetch(
            `${config.api.url}/admin/clients/${encodeURIComponent(clientReferenceId)}/password-reset`,
            {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(body)
            }
        );
        return handleResponse(res);
    }
};

export default adminApi;
